import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    BarChart3,
    Plus,
    Clock,
    Trash2,
    CheckCircle2,
    Users,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

const PollManager: React.FC = () => {
    const [polls, setPolls] = useState<any[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [question, setQuestion] = useState('');
    const [description, setDescription] = useState('');
    const [options, setOptions] = useState(['', '']);
    const [expiresAt, setExpiresAt] = useState('');
    const [pollResults, setPollResults] = useState<Record<string, any>>({});

    useEffect(() => { fetchPolls(); }, []);

    const fetchPolls = async () => {
        const res = await api.get('/polls');
        setPolls(res.data);
        res.data.forEach((p: any) => fetchResults(p.id));
    };

    const fetchResults = async (pollId: string) => {
        try {
            const res = await api.get(`/polls/${pollId}/results`);
            setPollResults(prev => ({ ...prev, [pollId]: res.data }));
        } catch (e) { console.error(e); }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/polls', {
                question, description, options: options.filter(o => o.trim()),
                expires_at: expiresAt || null
            });
            setQuestion(''); setDescription(''); setOptions(['', '']); setExpiresAt('');
            setIsCreating(false);
            fetchPolls();
        } catch (e) { alert('Poll creation failed'); }
    };

    const addOption = () => setOptions([...options, '']);

    return (
        <div className="space-y-6">
            <PageHeader title="Polls & Voting" subtitle="Gather resident feedback on society decisions">
                <button
                    onClick={() => setIsCreating(!isCreating)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isCreating ? 'Cancel' : 'New Poll'}
                    <Plus size={18} className={cn("transition-transform", isCreating && "rotate-45")} />
                </button>
            </PageHeader>

            {isCreating && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500">Poll Question</label>
                            <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. Upgrade gym equipment?" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Description</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Expiry Date</label>
                                <input value={expiresAt} onChange={e => setExpiresAt(e.target.value)} type="datetime-local" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-500">Choices</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {options.map((opt, idx) => (
                                    <input
                                        key={idx}
                                        value={opt}
                                        onChange={e => {
                                            const newOps = [...options];
                                            newOps[idx] = e.target.value;
                                            setOptions(newOps);
                                        }}
                                        placeholder={`Option ${idx + 1}`}
                                        className="w-full bg-white border border-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-violet-500"
                                    />
                                ))}
                            </div>
                            <button type="button" onClick={addOption} className="text-[10px] font-bold uppercase text-violet-600 hover:text-violet-800 transition-colors">
                                + Add Option
                            </button>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">
                                Create Poll
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {polls.map((p: any) => {
                    const isExpired = p.expires_at && new Date(p.expires_at) < new Date();
                    const results = pollResults[p.id];
                    return (
                        <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col hover:border-violet-200 transition-all group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600">
                                    <BarChart3 size={20} />
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <span className={cn(
                                        "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                        isExpired ? "bg-slate-50 text-slate-400 border-slate-100" : "bg-emerald-50 text-emerald-700 border-emerald-100"
                                    )}>
                                        {isExpired ? 'Closed' : 'Active'}
                                    </span>
                                    {p.expires_at && (
                                        <p className="text-[10px] text-slate-400 font-medium">
                                            {isExpired ? 'Ended' : 'Ends'}: {new Date(p.expires_at).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <h3 className="font-bold text-slate-900 mb-6">{p.question}</h3>

                            <div className="space-y-3 mb-6 flex-1">
                                {results?.results.map((r: any) => (
                                    <div key={r.option_id} className="space-y-1">
                                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            <span>{r.option_text}</span>
                                            <span>{r.votes} votes ({r.percentage}%)</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-violet-500 rounded-full transition-all duration-1000"
                                                style={{ width: `${r.percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-slate-400">
                                    <Users size={14} />
                                    <span className="text-[10px] font-bold uppercase">{results?.total_votes || 0} Total Votes</span>
                                </div>
                                <button className="p-1.5 text-slate-300 hover:text-red-500 transition-colors">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PollManager;
